import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TOKEN_SESSION_NAME } from 'core/auth.service';
import { API } from 'data/models/api.interface';
import { HttpErrorResponse } from 'data/models/error.interface';
import { FilterParams } from 'data/models/filter.interface';
import { useStorage } from 'plugins/storage';

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const httpErrorResponse: HttpErrorResponse = error?.response?.data;
    return { error: httpErrorResponse };
  },
);

export const useLaravelApi = (): API => {
  const send = (
    url: string,
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse> => {
    const storage = useStorage();
    const token = storage.get(TOKEN_SESSION_NAME);

    return axios(url, {
      responseType: 'json',
      baseURL: process.env.VITE_APP_BACK_API_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
      },
      ...config,
    });
  };

  const patch = <T extends object, U extends object>(
    url: string,
    body: Partial<T>,
  ): Promise<{ data?: U; error?: HttpErrorResponse }> => {
    return send(url, {
      data: body,
      method: 'PATCH',
    });
  };

  const post = <T extends object, U extends object>(
    url: string,
    body: Partial<T>,
  ): Promise<{ data?: U; error?: HttpErrorResponse }> => {
    return send(url, {
      data: body,
      method: 'POST',
    });
  };

  const destroy = <T extends object>(
    url: string,
  ): Promise<{ data?: T; error?: HttpErrorResponse }> => {
    return send(url, {
      method: 'DELETE',
    });
  };

  const get = <T extends object>(
    url: string,
    qs?: FilterParams,
  ): Promise<{ data?: T; error?: HttpErrorResponse }> => {
    const { filter, orderBy, orderDirection, ...params } = qs || {};

    const apiParams: {
      'page[size]': number | undefined;
      'page[number]': number | undefined;
      include?: string;
      sort?: string;
      [key: string]: string | number | string[] | undefined;
    } = {
      ['include']: params.include?.join(',') || '',
      ['page[size]']: params.itemsPerPage,
      ['page[number]']: params.page,
    };

    if (orderBy) {
      apiParams.sort = `${(orderDirection === 'desc' && '-') || ''}${orderBy}`;
    }

    return send(url, {
      params: Object.keys(filter || {}).reduce((acc, cur) => {
        if (!filter || !cur) {
          return acc;
        }

        const value = filter[cur];

        if (!value) {
          return acc;
        }

        acc[`filter[${cur}]`] = filter[cur];
        return acc;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, apiParams as any),
      method: 'GET',
    });
  };
  return {
    get,
    patch,
    post,
    destroy,
  };
};
